import styled from 'styled-components';
import { ThemePrisma } from '@orbita-ui/brand';
import { Icon } from '@orbita-ui/core';
import { ReactElement } from 'react';

type VariantColor = 'success' | 'error' | 'warning' | 'info';
const getColor = (color: VariantColor): string => {
  switch (color) {
    case 'error':
      return ThemePrisma.colors.red[5];
    case 'success':
      return ThemePrisma.colors.green[5];
    case 'warning':
      return ThemePrisma.colors.yellow[4];
    case 'info':
      return ThemePrisma.colors.blue[4];
    default:
      return 'transparent';
  }
};

const borderRadius = ThemePrisma.borderRadius.small;

const CardContainer = styled.div<{ $color?: VariantColor }>`
  width: 100%;
  position: relative;
  border-radius: ${borderRadius};
  background-color: ${ThemePrisma.colors.gray[1]};
  box-shadow: ${ThemePrisma.elevation[20]};

  &.with-color {
    gap: 4px;
    padding: 12px 16px;
    overflow: hidden;
    display: flex;
    line-height: 24px;

    .card-holder {
      width: 100%;
      padding-left: 12px;
    }

    &::before {
      content: '';
      position: absolute;
      top: 0px;
      left: 0px;
      width: 5%;
      height: 100%;
      border-radius: 1.5px;
      box-shadow: ${(props) => getColor(props.$color)} 8px 0px inset;
    }
  }
`;

const CardTitle = styled.div`
  font-family: ${ThemePrisma.fonts.title.s.midContrast.fontFamily};
  font-size: ${ThemePrisma.fonts.title.s.midContrast.fontSize};
  font-weight: ${ThemePrisma.fonts.title.s.midContrast.fontWeight};
  letter-spacing: ${ThemePrisma.fonts.title.s.midContrast.letterSpacing};
  line-height: ${ThemePrisma.fonts.title.s.midContrast.lineHeight};
  color: ${ThemePrisma.colors.gray[1]};
  background-color: ${ThemePrisma.colors.green[5]};
  border-bottom: 1px solid ${ThemePrisma.colors.green[6]};
  padding: ${ThemePrisma.spacing.medium};
  border-radius: ${borderRadius} ${borderRadius} 0 0;
`;

const CardBody = styled.div<{ $padding?: boolean; $spacing?: boolean }>`
  border-radius: 0 0 ${borderRadius} ${borderRadius};
  padding: ${(props) => (props.$padding ? ThemePrisma.spacing.medium : 0)};
  display: flex;
  flex-direction: column;
  gap: ${(props) => (props.$spacing ? ThemePrisma.spacing.small : 0)};
  line-height: 24px;
`;

const CardFooter = styled.div`
  padding: ${ThemePrisma.spacing.medium};
`;

const Card = ({
  children,
  variant,
  className,
}: {
  children: ReactElement<any, any>[] | ReactElement<any, any>;
  variant?: VariantColor;
  className?: string;
}) => {
  let icon = null;
  let color = null;
  switch (variant) {
    case 'error':
      icon = 'WarningTriangleFilledIcon';
      color = 'danger';
      break;
    case 'info':
      icon = 'InfoFilledIcon';
      color = 'info';
      break;
    case 'success':
      icon = 'CheckCircleFilledIcon';
      color = 'success';
      break;
    case 'warning':
      icon = 'WarningFilledIcon';
      color = 'warning';
      break;
  }
  return (
    <CardContainer
      className={className + ' ' + (variant ? 'with-color' : '')}
      $color={variant}
    >
      {variant && (
        <div className="icon-container">
          <Icon color={color} name={icon} />
        </div>
      )}
      <div className="card-holder">{children}</div>
    </CardContainer>
  );
};

export default Object.assign(Card, {
  Title: CardTitle,
  Body: CardBody,
  Footer: CardFooter,
});
